/**
 * Reports - Consolidated reports page with horizontal tabs
 * Combines three report views:
 * - End of Day Summary
 * - Override History
 * - Yield Variance
 */
import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useAuth } from '../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, History, TrendingDown, CheckCircle, ClipboardList, Database, ScrollText } from 'lucide-react';

// Import existing report page components
import EndOfDaySummary from './EndOfDaySummary';
import OverrideHistory from './OverrideHistory';
import YieldVarianceReport from './YieldVarianceReport';
import ApprovalHistory from './ApprovalHistory';
import ItemSummaryReport from './ItemSummaryReport';
import ProductionRecords from './ProductionRecords';
import AdminActivity from './AdminActivity';

type ReportTab = 'end-of-day' | 'override-history' | 'yield-variance' | 'approval-history' | 'item-summary-report' | 'production-records' | 'admin-activity';

const Reports: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  usePageTitle(t('reports.title'));
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'A' || user?.role === 'admin';

  // Get initial tab from URL or default to 'end-of-day'
  const initialTab = (searchParams.get('tab') as ReportTab) || 'end-of-day';
  const [activeTab, setActiveTab] = useState<ReportTab>(initialTab);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);

  const handleTabChange = (tab: ReportTab) => {
    if (tab === activeTab) {
      // Same tab clicked → toggle summary collapse
      setSummaryCollapsed(prev => !prev);
    } else {
      setActiveTab(tab);
      setSearchParams({ tab });
      setSummaryCollapsed(false);
    }
  };

  const tabs = useMemo(() => {
    const base = [
      {
        id: 'end-of-day' as ReportTab,
        label: t('nav.endOfDaySummary'),
        icon: BarChart3,
      },
      {
        id: 'yield-variance' as ReportTab,
        label: t('nav.yieldVariance'),
        icon: TrendingDown,
      },
      {
        id: 'override-history' as ReportTab,
        label: t('nav.overrideHistory'),
        icon: History,
      },
      {
        id: 'approval-history' as ReportTab,
        label: t('nav.approvalHistory'),
        icon: CheckCircle,
      },
      {
        id: 'item-summary-report' as ReportTab,
        label: t('nav.itemSummaryReport'),
        icon: ClipboardList,
      },
      {
        id: 'production-records' as ReportTab,
        label: t('nav.productionRecords'),
        icon: Database,
      },
    ];
    if (isAdmin) {
      base.push({
        id: 'admin-activity' as ReportTab,
        label: t('nav.adminActivity'),
        icon: ScrollText,
      });
    }
    return base;
  }, [isAdmin, t]);

  return (
    <div className="space-y-6">
      {/* Tab Buttons */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-tour={tab.id === 'end-of-day' ? 'report-eod' : tab.id === 'yield-variance' ? 'report-yield' : undefined}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4 inline-block mr-1 -mt-0.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'end-of-day' && <EndOfDaySummary embedded summaryCollapsed={summaryCollapsed} />}
      {activeTab === 'override-history' && <OverrideHistory embedded />}
      {activeTab === 'yield-variance' && <YieldVarianceReport embedded summaryCollapsed={summaryCollapsed} />}
      {activeTab === 'approval-history' && <ApprovalHistory embedded />}
      {activeTab === 'item-summary-report' && <ItemSummaryReport embedded />}
      {activeTab === 'production-records' && <ProductionRecords embedded />}
      {activeTab === 'admin-activity' && <AdminActivity embedded />}
    </div>
  );
};

export default Reports;
